/**
 * pages/RiwayatTransformasi.js
 * Read-only list of all past transformations with Rakit/Urai toggle and date-range filter.
 * Filtering is client-side only — writes nothing. Default range: last 7 days.
 *
 * `focusId` (section E3): set when reached from an item's riwayat ("Lihat transformasi"). That
 * run opens expanded, highlighted and scrolled into view, and the date range is widened just
 * enough to include it (an older run would otherwise sit outside the default 7 days).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fmtDate, fmtQty, today } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { findMatchingRecipe } from "../utils/recipeMatch";

/** Convert an ISO timestamp to a local "YYYY-MM-DD" string for boundary-safe date comparisons. */
const localDateStr = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

/** Return a local YYYY-MM-DD string `days` before the given YYYY-MM-DD string. */
const shiftDays = (dateStr, days) => {
  const d = new Date(dateStr + "T00:00:00"); // parse as local midnight
  d.setDate(d.getDate() + days);
  return localDateStr(d);
};

export default function RiwayatTransformasi({ transformations = [], history = [], items = [], recipes = [], focusId = null, onBack }) {
  // Read once at mount (App remounts this page per focusId).
  const [focus] = useState(() => {
    const t = focusId ? transformations.find((x) => x.id === focusId) : null;
    return t ? { id: t.id, day: localDateStr(t.date) } : null;
  });
  const [expanded, setExpanded] = useState(() => new Set(focus ? [focus.id] : []));
  const [typeFilter, setTypeFilter] = useState("ALL"); // "ALL" | "ASSEMBLE" | "TEARDOWN"
  const defaultFrom = shiftDays(today(), -6); // today − 6 days = 7-day window
  const [fromDate, setFromDate] = useState(() => (focus && focus.day < defaultFrom ? focus.day : defaultFrom));
  const [toDate, setToDate] = useState(() => (focus && focus.day > today() ? focus.day : today()));
  const [lastPreset, setLastPreset] = useState(() => (focus && (focus.day < defaultFrom || focus.day > today()) ? "" : "7"));
  const focusRef = useRef(null);
  useEffect(() => {
    // jsdom has no scrollIntoView — optional-chained past it (same as BusinessLinkModal).
    if (focus) focusRef.current?.scrollIntoView?.({ block: "center" });
  }, [focus]);

  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  const nameOf = (code) => byCode.get(code)?.name || code;
  const isSetOf = (code) => byCode.get(code)?.isSet === true;

  const titleFor = (t) => {
    const outs = t.outputs || [];
    const ins = t.inputs || [];
    if (t.type === "TEARDOWN") {
      const src = ins[0];
      return src ? nameOf(src.internalCode) : "—";
    }
    const setOuts = outs.filter((l) => isSetOf(l.internalCode));
    const nonSetCount = outs.length - setOuts.length;
    let named, extra;
    if (setOuts.length > 0) {
      named = setOuts.slice(0, 3).map((l) => nameOf(l.internalCode));
      extra = (setOuts.length > 3) || (nonSetCount > 0);
    } else {
      named = outs.slice(0, 3).map((l) => nameOf(l.internalCode));
      extra = outs.length > 3;
    }
    const base = named.join(", ") || "—";
    return extra ? base + " + lainnya" : base;
  };

  // ── Preset actions ──────────────────────────────────────────────────────────
  const applyPreset = (preset) => {
    const t = today();
    if (preset === "today") { setFromDate(t); setToDate(t); }
    else if (preset === "7")  { setFromDate(shiftDays(t, -6)); setToDate(t); }
    else if (preset === "30") { setFromDate(shiftDays(t, -29)); setToDate(t); }
    else { setFromDate(""); setToDate(""); } // "all"
    setLastPreset(preset);
  };

  // ── Filtered + sorted list ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = transformations;
    if (typeFilter !== "ALL") list = list.filter((t) => t.type === typeFilter);
    if (fromDate) list = list.filter((t) => localDateStr(t.date) >= fromDate);
    if (toDate)   list = list.filter((t) => localDateStr(t.date) <= toDate);
    return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transformations, typeFilter, fromDate, toDate]);

  // One matchedRecipe computed per visible row per actual data change, not per render — this
  // runs unconditionally for every visible row (unlike balancesFor, not gated on isOpen).
  const matchedRecipeById = useMemo(() => {
    const m = new Map();
    for (const t of filtered) {
      const inputs = t.inputs || [];
      const outputs = t.outputs || [];
      m.set(t.id, findMatchingRecipe(recipes, { type: t.type, inputs, outputs }));
    }
    return m;
  }, [filtered, recipes]);

  // ── Toggle expand ────────────────────────────────────────────────────────────
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // One-time Map build from history, keyed by ref|itemCode|sign, so each line lookup below is
  // O(1) instead of a full history scan — history has no natural size cap (see REFACTOR_CANDIDATES.md M5).
  const historyByRefCodeSign = useMemo(() => {
    const m = new Map();
    for (const e of history) {
      if (e.kind !== "TRANSFORMASI") continue;
      const sign = e.delta < 0 ? "in" : e.delta > 0 ? "out" : null;
      if (!sign) continue;
      const key = `${e.ref}|${e.itemCode}|${sign}`;
      if (!m.has(key)) m.set(key, e); // first match wins, same as Array.find above
    }
    return m;
  }, [history]);

  // Find the history entry for a line in a specific transform, matched by delta sign.
  const balancesFor = (t, code, sign) => {
    const h = historyByRefCodeSign.get(`${t.id}|${code}|${sign}`);
    if (!h) return { before: null, after: null };
    return { before: h.balanceAfter - h.delta, after: h.balanceAfter };
  };

  const renderLines = (t, lines, sign) =>
    lines.map((l, i) => {
      const { before, after } = balancesFor(t, l.internalCode, sign);
      return (
        <tr key={i}>
          <td><span className="mono">{l.internalCode}</span> · {nameOf(l.internalCode)}</td>
          <td className="num">× {fmtQty(l.qty)}</td>
          <td className="num">{before !== null ? fmtQty(before) : "—"}</td>
          <td className="num">{after !== null ? fmtQty(after) : "—"}</td>
        </tr>
      );
    });

  return (
    <div>
      {/* Header — unchanged from E-1 */}
      <div className="page-header">
        <div>
          <h1>Riwayat Transformasi</h1>
          <div className="page-sub">Semua transformasi yang pernah dijalankan · hanya-baca</div>
        </div>
        <button type="button" className="btn" onClick={onBack}>← Kembali ke Transformasi</button>
      </div>

      {/* Filter controls */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--gap-md)", alignItems: "center", marginBottom: "var(--gap-md)" }}>
        {/* Type toggle */}
        <div className="seg">
          <button type="button" className={typeFilter === "ALL" ? "active" : ""} onClick={() => setTypeFilter("ALL")}>Semua</button>
          <button type="button" className={typeFilter === "ASSEMBLE" ? "active" : ""} onClick={() => setTypeFilter("ASSEMBLE")}>Rakit</button>
          <button type="button" className={typeFilter === "TEARDOWN" ? "active" : ""} onClick={() => setTypeFilter("TEARDOWN")}>Urai</button>
        </div>

        {/* Date presets */}
        <div style={{ display: "flex", gap: "var(--gap-xs)" }}>
          {[["today", "Hari ini"], ["7", "7 hari"], ["30", "30 hari"], ["all", "Semua"]].map(([key, label]) => (
            <button key={key} type="button"
              className={"btn btn-sm" + (lastPreset === key ? " btn-primary" : "")}
              onClick={() => applyPreset(key)}>
              {label}
            </button>
          ))}
        </div>

        {/* Custom date inputs */}
        <div style={{ display: "flex", gap: "var(--gap-sm)", alignItems: "center" }}>
          <input type="date" className="form-input" style={{ width: 150 }}
            value={fromDate} onChange={(e) => { setFromDate(e.target.value); setLastPreset(""); }} />
          <span style={{ color: "var(--muted)", fontSize: 13 }}>s/d</span>
          <input type="date" className="form-input" style={{ width: 150 }}
            value={toDate} onChange={(e) => { setToDate(e.target.value); setLastPreset(""); }} />
        </div>
      </div>

      {/* Count (filtered) */}
      <div className="page-sub" style={{ marginBottom: "var(--gap-md)" }}>{filtered.length} transformasi</div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
          {transformations.length === 0
            ? "Belum ada transformasi."
            : "Tidak ada transformasi pada rentang / filter ini."}
        </div>
      ) : filtered.map((t) => {
        const isOpen = expanded.has(t.id);
        const inputs = t.inputs || [];
        const outputs = t.outputs || [];
        const matchedRecipe = matchedRecipeById.get(t.id);
        return (
          <div
            className={"txn-row" + (focus && t.id === focus.id ? " txn-row--focus" : "")}
            key={t.id}
            ref={focus && t.id === focus.id ? focusRef : undefined}
            data-testid={focus && t.id === focus.id ? "riwayat-focus" : undefined}
          >
            <button type="button" className="txn-row__head" onClick={() => toggle(t.id)}>
              <span style={{ color: "var(--muted)", fontSize: 13 }}>{isOpen ? "▾" : "▸"}</span>
              <span className={"chip " + (t.type === "ASSEMBLE" ? "chip--rakit" : "chip--urai")}>
                {t.type === "ASSEMBLE" ? "RAKIT" : "URAI"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {titleFor(t)}
                  {matchedRecipe && (
                    <span className="chip chip--resep" style={{ marginLeft: "var(--gap-sm)" }}>
                      Cocok resep: {matchedRecipe.name}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {inputs.length} input → {outputs.length} output · {bucketLabel(t.bucket)}
                </div>
              </div>
              <span className="txn-row__date">{fmtDate(t.date)}</span>
            </button>

            {isOpen && (
              <div className="txn-detail">
                <div style={{ marginBottom: "var(--gap-sm)" }}>
                  <div className="form-label" style={{ marginBottom: "var(--gap-xs)" }}>Input</div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Barang</th>
                          <th className="num">Qty</th>
                          <th className="num">Stok sebelum</th>
                          <th className="num">Stok sesudah</th>
                        </tr>
                      </thead>
                      <tbody>{renderLines(t, inputs, "in")}</tbody>
                    </table>
                  </div>
                </div>
                <div style={{ marginBottom: "var(--gap-sm)" }}>
                  <div className="form-label" style={{ marginBottom: "var(--gap-xs)" }}>Output</div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Barang</th>
                          <th className="num">Qty</th>
                          <th className="num">Stok sebelum</th>
                          <th className="num">Stok sesudah</th>
                        </tr>
                      </thead>
                      <tbody>{renderLines(t, outputs, "out")}</tbody>
                    </table>
                  </div>
                </div>
                {matchedRecipe && (
                  <div style={{ marginTop: "var(--gap-sm)", fontSize: 13, color: "#047857" }}>
                    Cocok resep: <b>{matchedRecipe.name}</b>
                  </div>
                )}
                {t.notes ? (
                  <div style={{ marginTop: "var(--gap-md)", padding: "10px 12px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, fontSize: 14 }}>
                    <span style={{ fontWeight: 600, color: "#92400e" }}>Catatan: </span>
                    <span style={{ color: "#1e293b" }}>{t.notes}</span>
                  </div>
                ) : (
                  <div style={{ marginTop: "var(--gap-sm)", color: "var(--muted)", fontSize: 12 }}>Catatan: —</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
